// Mentions légales et données d'un club (D-103) : le modèle de la page /donnees, rempli avec ce que le
// bureau règle (nom officiel, siège, représentant légal, e-mail pour les données) ; « à compléter » tant
// que c'est vide. Le club est responsable du traitement ; IDR, éditeur d'Arceau, l'héberge pour lui
// (sous-traitant). Vouvoiement (page publique), pas d'écriture inclusive (D-008). Les faits viennent de
// l'app elle-même et de ce qu'IDR a donné ; le texte est un MODÈLE, à faire relire par un juriste.

export interface ClubLegal {
  name: string; short_name: string | null; logo: string | null; color: string | null;
  slug: string | null; public: boolean;
  legal_name: string | null; address: string | null; rep: string | null;
  rep_role: RepRole | null; data_email: string | null; contact_email: string | null;
}
export type RepRole = 'président' | 'présidente' | 'représentant légal' | 'représentante légale';
export const REP_ROLES: RepRole[] = ['président', 'présidente', 'représentant légal', 'représentante légale'];

/** Les faits d'IDR et de ses prestataires (donnés par IDR). */
export const IDR = {
  name: 'IDR', form: 'société par actions simplifiée (SAS)', siren: '931 177 117',
  address: '6 boulevard Marcel Paul, 31170 Tournefeuille',
} as const;
export const HOSTING = { name: 'Hostinger International Ltd', address: '61 Lordou Vironos Street, 6023 Larnaca, Chypre' } as const;
export const MAILER = 'Brevo';
export const SITE_URL = 'https://arceau.app';
export const PRIVACY_URL = `${SITE_URL}/confidentialite`;
export const MENTIONS_URL = `${SITE_URL}/mentions-legales`;
export const CNIL_URL = 'https://www.cnil.fr';

/** Le bandeau en tête de chaque texte juridique. */
export const DRAFT_NOTE = 'À faire relire par un juriste avant l’ouverture de l’app aux familles.';

/** Un morceau de texte : du texte, en gras, un lien, ou un champ « à compléter ». */
export type Part = string | { b: string } | { a: string; href: string } | { todo: string };
export type Block = { p: Part[] } | { ul: Part[][] };
export interface LegalSection { id: string; title: string; blocks: Block[] }

const clean = (s: string | null | undefined) => (s ?? '').trim();
const todo = (what: string): Part => ({ todo: `${what} : à compléter par le bureau` });

/** Le nom du responsable du traitement : le nom officiel, sinon celui du club. */
export const controllerName = (c: Pick<ClubLegal, 'name' | 'legal_name'>) => clean(c.legal_name) || c.name;
/** L'adresse où exercer ses droits : celle des données, sinon le contact public du club. */
export const rightsEmail = (c: Pick<ClubLegal, 'data_email' | 'contact_email'>) => clean(c.data_email) || clean(c.contact_email) || null;

/** Ce que le bureau doit encore compléter (le réglage du club le montre). */
export function legalMissing(c: Pick<ClubLegal, 'address' | 'rep' | 'rep_role' | 'data_email' | 'contact_email'>): string[] {
  const out: string[] = [];
  if (!clean(c.address)) out.push('l’adresse du siège');
  if (!clean(c.rep) || !c.rep_role) out.push('le représentant légal');
  if (!rightsEmail(c)) out.push('l’e-mail pour les données');
  return out;
}

/** « Nathalie Martin, présidente », sinon à compléter. */
function repLine(c: ClubLegal): Part {
  const rep = clean(c.rep);
  if (!rep || !c.rep_role) return todo('nom et qualité du représentant légal');
  return `${rep}, ${c.rep_role}`;
}
const mail = (e: string | null): Part => (e ? { a: e, href: `mailto:${e}` } : todo('e-mail pour les données'));

/** La page « Mentions légales et données » du club, section par section. */
export function clubDataSections(c: ClubLegal): LegalSection[] {
  const who = controllerName(c);
  const email = rightsEmail(c);
  return [
    { id: 'responsable', title: 'Qui est responsable de vos données', blocks: [
      { p: [{ b: who }, ' est responsable du traitement des données de ses membres dans l’app du club : il décide de ce qui y est enregistré et de qui le voit.'] },
      { ul: [
        ['Siège : ', clean(c.address) || todo('adresse du siège')],
        ['Représentant légal : ', repLine(c)],
        ['Contact pour vos données : ', mail(email)],
      ] },
    ] },
    { id: 'hebergeur', title: 'Hébergement et sous-traitance', blocks: [
      { p: ['L’app du club fonctionne avec Arceau, éditée par ', { b: IDR.name }, `, ${IDR.form}, SIREN ${IDR.siren}, ${IDR.address}. `,
        'IDR héberge les données pour le compte du club et agit comme son sous-traitant : il ne s’en sert que pour faire fonctionner l’app.'] },
      { ul: [
        ['Hébergement : ', `${HOSTING.name}, ${HOSTING.address}.`],
        ['Envoi des e-mails (codes de connexion, invitations) : ', `${MAILER}.`],
        ['Sauvegardes : chaque nuit, chiffrées.'],
      ] },
    ] },
    { id: 'finalites', title: 'Ce que l’app enregistre, et pourquoi', blocks: [
      { ul: [
        [{ b: 'La vie sportive' }, ' : équipes, entraînements, matchs, convocations, présences et absences. Le motif d’une absence n’est vu que par le staff de l’équipe (et par les parents, pour un enfant).'],
        [{ b: 'Les tirages d’équipes' }, ' : la fiche « Pour les tirages » de chaque joueur (un niveau sur trois crans, un ou deux postes, fille ou garçon dans une équipe mixte) sert à faire des équipes équilibrées à l’entraînement. Seuls le staff de l’équipe et le responsable technique la voient : ni le joueur, ni ses parents, ni les autres équipes ; jamais sur un écran de salle ni sur la page publique.'],
        [{ b: 'Les licences' }, ' : le suivi des dossiers par le bureau (pièces reçues, paiements). Les pièces médicales restent dans e-Licence (FFBB).'],
        [{ b: 'La vie du club' }, ' : annonces, notifications, agenda, bénévolat, covoiturage, badges.'],
        [{ b: 'Les partenaires' }, ' : leur visibilité se compte en affichages et en clics, jamais par personne.'],
        [{ b: 'Les demandes' }, ' de la page du club (séance d’essai, bénévolat, partenariat) : seulement pour y répondre.'],
      ] },
      { p: ['Les données utiles : prénom, nom, date de naissance et équipe de chaque licencié ; e-mail et téléphone des adultes et des parents ; ce que chacun écrit dans l’app. Une indisponibilité peut être marquée « raison médicale » : seule la case est enregistrée, jamais la nature du problème.'] },
    ] },
    { id: 'acces', title: 'Qui les voit', blocks: [
      { ul: [
        ['Le bureau du club.'],
        ['Les coachs et le staff, pour les équipes qu’ils encadrent (dont le téléphone et l’e-mail des joueurs et de leurs parents).'],
        ['Chaque membre, ce qui le concerne et ce que le club partage : son équipe, l’agenda, les annonces qui lui sont adressées, les créneaux de bénévoles (avec le prénom de ceux qui les tiennent).'],
        ['IDR, pour l’hébergement et le support, seulement quand c’est nécessaire.'],
      ] },
      { p: ['Aucune donnée n’est vendue ni utilisée pour de la publicité.'] },
    ] },
    { id: 'durees', title: 'Combien de temps', blocks: [
      { ul: [
        ['Les données d’un membre restent tant qu’il fait partie du club ; ensuite : ', todo('durée de conservation après le départ')],
        ['Les demandes reçues par la page du club : le temps d’y répondre ; ensuite : ', todo('durée de conservation des demandes')],
        ['Un lien d’invitation sert une fois et expire après 7 jours ; un lien de saisie des stats ne vaut que pendant le match.'],
        ['La fiche « Pour les tirages » : une seule valeur, sans historique, effacée en fin de saison.'],
      ] },
    ] },
    { id: 'droits', title: 'Vos droits', blocks: [
      { p: ['Vous pouvez demander à voir vos données, à les faire corriger ou effacer, vous opposer à leur utilisation, en limiter l’usage ou les recevoir pour les emporter ailleurs. Pour un enfant, ses parents peuvent le demander pour lui.'] },
      { p: ['Pour cela, écrivez au club : ', mail(email), '. Le prénom de votre compte se corrige aussi dans l’app, Moi › Mon compte.'] },
      { p: ['Si vous estimez que vos droits ne sont pas respectés, vous pouvez adresser une réclamation à la CNIL : ', { a: 'cnil.fr', href: CNIL_URL }, '.'] },
    ] },
    { id: 'mineurs', title: 'Les enfants et le droit à l’image', blocks: [
      { p: ['Un enfant sans adresse e-mail se connecte avec un code perso, créé par un parent ou par le coach. Ses parents suivent tout depuis leur propre compte.'] },
      { ul: [
        ['Le compte d’un enfant (code perso ou moins de 18 ans) ne montre aucune offre de partenaire, et ses visites ne sont pas comptées pour les partenaires.'],
        ['Il ne propose pas de conduire au covoiturage.'],
        ['Il ne prend que les créneaux de bénévolat que le bureau ouvre aux jeunes, et n’entre dans aucun groupe de bénévoles.'],
        ['Ses parents sont prévenus de ses absences, de ses créneaux et de ses inscriptions.'],
        ['Il n’apparaît pas dans l’annonce « Bravo à… » du lundi, sauf s’il le choisit.'],
      ] },
      { p: ['La page publique du club ne montre ni nom ni photo de joueur. Une photo de victoire n’y est publiée par le staff qu’avec l’accord de chaque personne visible, et celui des parents pour un enfant. Dans l’app, les photos d’un match ne sont vues que par l’équipe et son staff.'] },
      { p: ['Les parents donnent dans l’app leurs accords pour les photos (oui, seulement dans l’app, non) et pour le transport de leur enfant aux matchs ; sans réponse, pas de photo. L’app ne reconnaît personne sur les images : celui qui publie une photo certifie que les enfants sans accord n’y sont pas, et un parent peut masquer une photo où se trouve son enfant. Les documents du dossier signés en ligne (rédigés dans l’app ou déposés en PDF) sont ceux du club : il en est responsable, ainsi que de leur conservation ; Arceau les héberge pour lui. Chaque version est gardée une seule fois, avec son empreinte ; une signature est une petite fiche (le nom, la qualité, la case cochée, la date et l’heure, la version), d’où le récépissé se refait à la demande. Les signatures et les anciennes versions sont effacées à la fin de la saison suivante ; le club peut les exporter avant.'] },
    ] },
    { id: 'cookies', title: 'Cookies', blocks: [
      { p: ['L’app n’utilise aucun cookie : ni mesure d’audience, ni publicité. Elle garde sur votre appareil (stockage local du navigateur) votre connexion et quelques réglages, comme le club affiché ou la casquette choisie. Les polices de caractères sont servies avec l’app.'] },
      { p: ['Si vous activez les notifications, elles passent, chiffrées, par le service de notification de votre navigateur.'] },
    ] },
  ];
}
